import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { Plus, Truck, User, Pencil, Trash2, UserPlus, UserMinus, Link as LinkIcon, Wrench, AlertTriangle } from "lucide-react";
import { useCanDelete } from "@/context/AuthContext";

const EMPTY_BOWSER = { registration_no: "", capacity_litres: 6000, compartments: 1, opening_dispenser_reading: 0, insurance_expiry: "", permit_expiry: "", fitness_expiry: "", puc_expiry: "", calibration_expiry: "" };
const EMPTY_DRIVER = { name: "", phone: "", license_no: "", license_expiry: "", address: "", aadhaar: "" };

const expiringSoon = (date) => {
  if (!date) return false;
  const days = (new Date(date) - new Date()) / 86400000;
  return days < 60 && days > -365;
};

export default function Fleet() {
  const { data: bowsers } = useQuery({ queryKey: ["fleet-bowsers-full"], queryFn: () => api.get("/fleet/bowsers").then((r) => r.data) });
  const { data: drivers } = useQuery({ queryKey: ["fleet-drivers"], queryFn: () => api.get("/fleet/drivers").then((r) => r.data) });

  return (
    <div className="space-y-6" data-testid="fleet-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Fleet</h1>
        <p className="text-sm text-muted-foreground">Bowsers · Drivers · Assignments · Compliance</p>
      </div>

      <Tabs defaultValue="bowsers">
        <TabsList>
          <TabsTrigger value="bowsers" data-testid="tab-bowsers"><Truck size={14} className="mr-1" /> Bowsers ({bowsers?.items?.length || 0})</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="tab-drivers"><User size={14} className="mr-1" /> Drivers ({drivers?.items?.length || 0})</TabsTrigger>
          <TabsTrigger value="service" data-testid="tab-service"><Wrench size={14} className="mr-1" /> Service & Breakdown</TabsTrigger>
        </TabsList>

        <TabsContent value="bowsers" className="mt-4 space-y-3">
          <BowserPanel bowsers={bowsers?.items || []} drivers={drivers?.items || []} />
        </TabsContent>
        <TabsContent value="drivers" className="mt-4">
          <DriverPanel drivers={drivers?.items || []} bowsers={bowsers?.items || []} />
        </TabsContent>
        <TabsContent value="service" className="mt-4">
          <ServicePanel bowsers={bowsers?.items || []} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ============================ BOWSERS ============================ */
function BowserPanel({ bowsers, drivers }) {
  const qc = useQueryClient();
  const canDelete = useCanDelete();
  const [open, setOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [assignBowser, setAssignBowser] = useState(null);
  const [form, setForm] = useState(EMPTY_BOWSER);
  const [driverPick, setDriverPick] = useState("");
  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e });

  const save = useMutation({
    mutationFn: () => (editingId ? api.patch(`/fleet/bowsers/${editingId}`, form) : api.post("/fleet/bowsers", form)),
    onSuccess: () => { toast.success(editingId ? "Bowser updated" : "Bowser added"); qc.invalidateQueries({ queryKey: ["fleet-bowsers-full"] }); setOpen(false); setEditingId(null); setForm(EMPTY_BOWSER); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/fleet/bowsers/${id}`),
    onSuccess: () => { toast.success("Bowser deleted"); qc.invalidateQueries({ queryKey: ["fleet-bowsers-full"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });
  const assign = useMutation({
    mutationFn: () => api.post(`/fleet/bowsers/${assignBowser.id}/assign-driver`, { driver_id: driverPick }),
    onSuccess: () => { toast.success("Driver assigned"); qc.invalidateQueries({ queryKey: ["fleet-bowsers-full"] }); qc.invalidateQueries({ queryKey: ["fleet-drivers"] }); setAssignOpen(false); setDriverPick(""); },
    onError: (e) => toast.error(e.response?.data?.detail || "Assignment failed"),
  });
  const unassign = useMutation({
    mutationFn: (id) => api.post(`/fleet/bowsers/${id}/unassign-driver`),
    onSuccess: () => { toast.success("Driver released"); qc.invalidateQueries({ queryKey: ["fleet-bowsers-full"] }); qc.invalidateQueries({ queryKey: ["fleet-drivers"] }); },
  });

  const openEdit = (b) => { setForm({ ...EMPTY_BOWSER, ...b }); setEditingId(b.id); setOpen(true); };
  const openNew = () => { setForm(EMPTY_BOWSER); setEditingId(null); setOpen(true); };
  const openAssign = (b) => { setAssignBowser(b); setDriverPick(b.assigned_driver_id || ""); setAssignOpen(true); };

  const availableDrivers = drivers.filter((d) => d.status !== "on_trip");

  return (
    <>
      <div className="flex items-center justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} data-testid="btn-new-bowser"><Plus size={14} className="mr-1" /> New bowser</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingId ? "Edit bowser" : "New bowser"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Registration No</Label><Input value={form.registration_no} onChange={upd("registration_no")} placeholder="MH-14-CD-1234" data-testid="bw-regno" /></div>
              <div><Label>Capacity (L)</Label><Input type="number" value={form.capacity_litres} onChange={upd("capacity_litres")} data-testid="bw-capacity" /></div>
              <div><Label>Compartments</Label><Input type="number" value={form.compartments} onChange={upd("compartments")} /></div>
              <div>
                <Label>Opening dispenser meter (L)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.opening_dispenser_reading || 0}
                  onChange={upd("opening_dispenser_reading")}
                  data-testid="bw-opening-reading"
                  placeholder="e.g. 0 (new) or 15200"
                />
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Meter value at the time this bowser was inducted. Used as the baseline for delivery meter checks. Leave 0 for a brand-new bowser.
                </div>
              </div>
              <div><Label>Insurance expiry</Label><Input type="date" value={(form.insurance_expiry || "").slice(0, 10)} onChange={upd("insurance_expiry")} /></div>
              <div><Label>Permit expiry</Label><Input type="date" value={(form.permit_expiry || "").slice(0, 10)} onChange={upd("permit_expiry")} /></div>
              <div><Label>Fitness expiry</Label><Input type="date" value={(form.fitness_expiry || "").slice(0, 10)} onChange={upd("fitness_expiry")} /></div>
              <div><Label>PUC expiry</Label><Input type="date" value={(form.puc_expiry || "").slice(0, 10)} onChange={upd("puc_expiry")} /></div>
              <div><Label>Calibration expiry</Label><Input type="date" value={(form.calibration_expiry || "").slice(0, 10)} onChange={upd("calibration_expiry")} /></div>
              {editingId && (
                <>
                  <div><Label>Odometer (km)</Label><Input type="number" value={form.odometer_km || 0} onChange={upd("odometer_km")} /></div>
                  <div><Label>Mileage (km/L)</Label><Input type="number" step="0.1" value={form.fuel_consumption_kmpl || 0} onChange={upd("fuel_consumption_kmpl")} /></div>
                </>
              )}
            </div>
            <DialogFooter><Button onClick={() => save.mutate()} disabled={!form.registration_no || save.isPending} data-testid="btn-save-bowser">{save.isPending ? "Saving…" : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {bowsers.map((b) => (
          <Card key={b.id} data-testid={`bowser-card-${b.code}`}>
            <CardHeader className="flex flex-col gap-3 pb-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{b.code}</div>
                <CardTitle className="font-display text-lg">{b.registration_no}</CardTitle>
                {b.tata_vin ? (
                  <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-primary" data-testid={`bw-tata-vin-${b.code}`}>
                    <Truck size={10} /> Tata · {b.tata_vin}
                  </div>
                ) : null}
              </div>
              <StatusBadge status={b.status} />
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="font-mono text-xl font-bold">{Math.round(b.current_stock || 0)}L</div>
                  <div className="text-xs text-muted-foreground">of {b.capacity_litres}L · {b.compartments} compartment{b.compartments > 1 ? "s" : ""}</div>
                </div>
                <div className="text-right text-xs">
                  <div>Odometer: <b className="font-mono">{b.odometer_km?.toLocaleString() || 0} km</b></div>
                  <div>Mileage: <b className="font-mono">{b.fuel_consumption_kmpl} km/L</b></div>
                </div>
              </div>
              <Progress value={b.capacity_litres ? (b.current_stock / b.capacity_litres) * 100 : 0} className="h-1.5" />

              <div className="grid grid-cols-2 gap-2 border-t border-border/40 pt-3 text-xs">
                <Compliance label="Insurance" date={b.insurance_expiry} warn={expiringSoon(b.insurance_expiry)} />
                <Compliance label="Permit" date={b.permit_expiry} warn={expiringSoon(b.permit_expiry)} />
                <Compliance label="Fitness" date={b.fitness_expiry} warn={expiringSoon(b.fitness_expiry)} />
                <Compliance label="Calibration" date={b.calibration_expiry} warn={expiringSoon(b.calibration_expiry)} />
                <Compliance label="PUC" date={b.puc_expiry} warn={expiringSoon(b.puc_expiry)} />
                <Compliance label="Last service" date={b.last_maintenance} />
              </div>

              <div className="flex items-center justify-between border-t border-border/40 pt-3">
                <div className="flex-1 text-xs">
                  <div className="text-muted-foreground uppercase tracking-widest">Assigned driver</div>
                  {b.assigned_driver_name ? (
                    <div className="flex items-center gap-2"><User size={12} className="text-primary" /><span className="font-semibold">{b.assigned_driver_name}</span></div>
                  ) : (
                    <div className="text-muted-foreground italic">— unassigned —</div>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => openAssign(b)} data-testid={`bw-assign-${b.code}`}><LinkIcon size={12} className="mr-1" />{b.assigned_driver_id ? "Reassign" : "Assign"}</Button>
                  {b.assigned_driver_id && <Button size="sm" variant="ghost" onClick={() => unassign.mutate(b.id)} data-testid={`bw-unassign-${b.code}`}><UserMinus size={12} /></Button>}
                  <Button size="sm" variant="ghost" onClick={() => openEdit(b)} data-testid={`bw-edit-${b.code}`}><Pencil size={12} /></Button>
                  {canDelete && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`bw-del-${b.code}`}><Trash2 size={12} /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete {b.registration_no}?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the bowser {b.code} and release its assigned driver. Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del.mutate(b.id)} data-testid={`bw-del-confirm-${b.code}`}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {bowsers.length === 0 && (
          <div className="col-span-full rounded-lg border-2 border-dashed border-border/60 p-12 text-center">
            <Truck size={36} className="mx-auto mb-3 opacity-40" />
            <div className="font-semibold">No bowsers yet</div>
            <div className="mt-1 text-sm text-muted-foreground">Add your first bowser to start dispatching orders.</div>
            <Button className="mt-4" onClick={openNew}><Plus size={14} className="mr-1" /> New bowser</Button>
          </div>
        )}
      </div>

      {/* Assign driver dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign driver to {assignBowser?.registration_no}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Select driver</Label>
              <Select value={driverPick} onValueChange={setDriverPick}>
                <SelectTrigger data-testid="assign-driver-select"><SelectValue placeholder="Choose driver" /></SelectTrigger>
                <SelectContent>
                  {availableDrivers.map((d) => (
                    <SelectItem key={d.id} value={d.id} disabled={d.status === "on_trip"}>
                      {d.code} — {d.name} · {d.phone} {d.status === "on_trip" ? "(on trip)" : ""}
                    </SelectItem>
                  ))}
                  {availableDrivers.length === 0 && <SelectItem value="none" disabled>No drivers available</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded border border-primary/20 bg-primary/5 p-2 text-xs text-muted-foreground">
              Assigning will release any previous driver on this bowser and unassign the selected driver from other bowsers.
            </div>
          </div>
          <DialogFooter><Button onClick={() => assign.mutate()} disabled={!driverPick || assign.isPending} data-testid="btn-confirm-assign">{assign.isPending ? "Assigning…" : "Assign driver"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ============================ DRIVERS ============================ */
function DriverPanel({ drivers, bowsers }) {
  const qc = useQueryClient();
  const canDelete = useCanDelete();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_DRIVER);
  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = useMutation({
    mutationFn: () => (editingId ? api.patch(`/fleet/drivers/${editingId}`, form) : api.post("/fleet/drivers", form)),
    onSuccess: () => { toast.success(editingId ? "Driver updated" : "Driver added"); qc.invalidateQueries({ queryKey: ["fleet-drivers"] }); qc.invalidateQueries({ queryKey: ["fleet-bowsers-full"] }); setOpen(false); setEditingId(null); setForm(EMPTY_DRIVER); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/fleet/drivers/${id}`),
    onSuccess: () => { toast.success("Driver deleted"); qc.invalidateQueries({ queryKey: ["fleet-drivers"] }); qc.invalidateQueries({ queryKey: ["fleet-bowsers-full"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  const openEdit = (d) => { setForm({ ...EMPTY_DRIVER, ...d }); setEditingId(d.id); setOpen(true); };
  const openNew = () => { setForm(EMPTY_DRIVER); setEditingId(null); setOpen(true); };

  const bowserByDriver = (id) => bowsers.find((b) => b.assigned_driver_id === id);

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} data-testid="btn-new-driver"><UserPlus size={14} className="mr-1" /> New driver</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingId ? "Edit driver" : "New driver"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={upd("name")} data-testid="drv-name" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={upd("phone")} placeholder="+91..." data-testid="drv-phone" /></div>
              <div><Label>Aadhaar</Label><Input value={form.aadhaar} onChange={upd("aadhaar")} /></div>
              <div><Label>License No</Label><Input value={form.license_no} onChange={upd("license_no")} data-testid="drv-license" /></div>
              <div><Label>License expiry</Label><Input type="date" value={(form.license_expiry || "").slice(0, 10)} onChange={upd("license_expiry")} /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={upd("address")} /></div>
            </div>
            <DialogFooter><Button onClick={() => save.mutate()} disabled={!form.name || !form.phone || save.isPending} data-testid="btn-save-driver">{save.isPending ? "Saving…" : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">License</th>
                <th className="px-4 py-3">Expiry</th>
                <th className="px-4 py-3">Assigned bowser</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((d) => {
                const bw = bowserByDriver(d.id);
                return (
                  <tr key={d.id} className="border-b border-border/40 text-sm" data-testid={`driver-row-${d.code}`}>
                    <td className="px-4 py-3 font-mono font-semibold">{d.code}</td>
                    <td className="px-4 py-3">{d.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{d.phone}</td>
                    <td className="px-4 py-3 font-mono text-xs">{d.license_no}</td>
                    <td className={`px-4 py-3 text-xs ${expiringSoon(d.license_expiry) ? "font-bold text-status-cancelled" : ""}`}>{d.license_expiry || "—"}</td>
                    <td className="px-4 py-3 text-xs">{bw ? <span className="rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-primary">{bw.registration_no}</span> : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`drv-edit-${d.code}`}><Pencil size={12} /></Button>
                        {canDelete && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`drv-del-${d.code}`}><Trash2 size={12} /></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Delete {d.name}?</AlertDialogTitle><AlertDialogDescription>This will permanently remove driver {d.code} and detach them from any assigned bowser.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => del.mutate(d.id)} data-testid={`drv-del-confirm-${d.code}`}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {drivers.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">No drivers yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </>
  );
}

const Compliance = ({ label, date, warn }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={warn ? "font-mono font-bold text-status-cancelled" : "font-mono"}>{date ? String(date).slice(0, 10) : "—"}</span>
  </div>
);

/* ============================ SERVICE & BREAKDOWN ============================ */
const EMPTY_MAINT = {
  bowser_id: "", service_type: "routine_service", description: "",
  cost: 0, date: new Date().toISOString().slice(0, 10),
  odometer_km: 0, vendor: "", invoice_no: "",
  next_due_date: "", next_due_km: 0, downtime_hours: 0,
  status: "completed", notes: "",
};

const SERVICE_TYPES = [
  { v: "routine_service", l: "Routine service" },
  { v: "breakdown", l: "Breakdown / Repair" },
  { v: "tyre", l: "Tyre change" },
  { v: "battery", l: "Battery" },
  { v: "brake", l: "Brake" },
  { v: "oil_change", l: "Oil change" },
  { v: "inspection", l: "Inspection" },
  { v: "repair", l: "Other repair" },
];

function ServicePanel({ bowsers }) {
  const qc = useQueryClient();
  const canDelete = useCanDelete();
  const [filterBowser, setFilterBowser] = useState("");
  const [filterType, setFilterType] = useState("");
  const { data } = useQuery({
    queryKey: ["maintenance", filterBowser, filterType],
    queryFn: () => api.get(`/fleet/maintenance?bowser_id=${filterBowser}&service_type=${filterType}`).then((r) => r.data),
  });
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_MAINT);
  const upd = (k) => (e) => setForm({ ...form, [k]: e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e });

  const save = useMutation({
    mutationFn: () => (editingId ? api.patch(`/fleet/maintenance/${editingId}`, form) : api.post("/fleet/maintenance", form)),
    onSuccess: () => { toast.success(editingId ? "Record updated" : "Record added"); qc.invalidateQueries({ queryKey: ["maintenance"] }); qc.invalidateQueries({ queryKey: ["fleet-bowsers-full"] }); qc.invalidateQueries({ queryKey: ["service-due"] }); setOpen(false); setEditingId(null); setForm(EMPTY_MAINT); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/fleet/maintenance/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["maintenance"] }); qc.invalidateQueries({ queryKey: ["service-due"] }); },
  });

  const openNew = () => { setForm(EMPTY_MAINT); setEditingId(null); setOpen(true); };
  const openEdit = (m) => { setForm({ ...EMPTY_MAINT, ...m, date: (m.date || "").slice(0, 10), next_due_date: (m.next_due_date || "").slice(0, 10) }); setEditingId(m.id); setOpen(true); };

  const totalCost = (data?.items || []).reduce((s, m) => s + (m.cost || 0), 0);
  const openBreakdowns = (data?.items || []).filter((m) => m.service_type === "breakdown" && m.status !== "completed").length;

  return (
    <>
      <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="kpi-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Records</div>
          <div className="mt-2 font-display text-2xl font-black">{data?.items?.length || 0}</div>
        </div>
        <div className="kpi-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Total spend</div>
          <div className="mt-2 font-display text-2xl font-black">₹{totalCost.toLocaleString("en-IN")}</div>
        </div>
        <div className="kpi-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1"><AlertTriangle size={12} /> Open breakdowns</div>
          <div className={`mt-2 font-display text-2xl font-black ${openBreakdowns ? "text-status-cancelled" : ""}`}>{openBreakdowns}</div>
        </div>
        <div className="kpi-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Downtime (hrs)</div>
          <div className="mt-2 font-display text-2xl font-black">{(data?.items || []).reduce((s, m) => s + (m.downtime_hours || 0), 0).toFixed(1)}</div>
        </div>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <div className="flex flex-1 items-center gap-2">
            <Select value={filterBowser || "all"} onValueChange={(v) => setFilterBowser(v === "all" ? "" : v)}>
              <SelectTrigger className="max-w-52" data-testid="filter-bowser"><SelectValue placeholder="All bowsers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All bowsers</SelectItem>
                {bowsers.map((b) => (<SelectItem key={b.id} value={b.id}>{b.code} — {b.registration_no}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
              <SelectTrigger className="max-w-52" data-testid="filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All service types</SelectItem>
                {SERVICE_TYPES.map((s) => (<SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} data-testid="btn-new-maint"><Plus size={14} className="mr-1" /> Add record</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editingId ? "Edit service record" : "Add service / breakdown record"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Bowser</Label>
                  <Select value={form.bowser_id} onValueChange={upd("bowser_id")}>
                    <SelectTrigger data-testid="maint-bowser"><SelectValue placeholder="Select bowser" /></SelectTrigger>
                    <SelectContent>
                      {bowsers.map((b) => (<SelectItem key={b.id} value={b.id}>{b.code} — {b.registration_no}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Service type</Label>
                  <Select value={form.service_type} onValueChange={upd("service_type")}>
                    <SelectTrigger data-testid="maint-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{SERVICE_TYPES.map((s) => (<SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={upd("description")} placeholder="What was done?" data-testid="maint-desc" /></div>
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={upd("date")} data-testid="maint-date" /></div>
                <div><Label>Cost (₹)</Label><Input type="number" step="0.01" value={form.cost} onChange={upd("cost")} data-testid="maint-cost" /></div>
                <div><Label>Odometer @ service (km)</Label><Input type="number" value={form.odometer_km} onChange={upd("odometer_km")} data-testid="maint-odo" /></div>
                <div><Label>Downtime (hours)</Label><Input type="number" step="0.5" value={form.downtime_hours} onChange={upd("downtime_hours")} /></div>
                <div><Label>Vendor / Workshop</Label><Input value={form.vendor} onChange={upd("vendor")} placeholder="MRF Service Point" /></div>
                <div><Label>Invoice / bill no</Label><Input value={form.invoice_no} onChange={upd("invoice_no")} /></div>
                <div><Label>Next due date</Label><Input type="date" value={form.next_due_date} onChange={upd("next_due_date")} data-testid="maint-due-date" /></div>
                <div><Label>Next due odometer (km)</Label><Input type="number" value={form.next_due_km} onChange={upd("next_due_km")} data-testid="maint-due-km" /></div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={upd("status")}>
                    <SelectTrigger data-testid="maint-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={upd("notes")} /></div>
              </div>
              <DialogFooter><Button onClick={() => save.mutate()} disabled={!form.bowser_id || !form.description || save.isPending} data-testid="btn-save-maint">{save.isPending ? "Saving…" : "Save"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Bowser</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2 text-right">Odo (km)</th>
                <th className="px-4 py-2 text-right">Cost</th>
                <th className="px-4 py-2">Next due</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((m) => (
                <tr key={m.id} className="border-b border-border/40 text-sm" data-testid={`maint-row-${m.id}`}>
                  <td className="px-4 py-2 font-mono">{m.bowser_code}</td>
                  <td className="px-4 py-2 text-xs">{m.date?.slice(0, 10)}</td>
                  <td className="px-4 py-2 text-xs uppercase">
                    {m.service_type === "breakdown"
                      ? <span className="rounded-full bg-status-cancelled/10 px-2 py-0.5 font-semibold text-status-cancelled">Breakdown</span>
                      : m.service_type.replace(/_/g, " ")}
                  </td>
                  <td className="px-4 py-2 text-xs max-w-64 truncate">{m.description}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{m.odometer_km ? m.odometer_km.toLocaleString() : "—"}</td>
                  <td className="px-4 py-2 text-right font-mono">₹{(m.cost || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2 text-xs">
                    {m.next_due_date && <div>{m.next_due_date.slice(0, 10)}</div>}
                    {m.next_due_km ? <div className="font-mono text-muted-foreground">{m.next_due_km.toLocaleString()} km</div> : null}
                    {!m.next_due_date && !m.next_due_km && "—"}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <span className={`rounded-full px-2 py-0.5 font-semibold uppercase ${m.status === "completed" ? "bg-status-delivered/10 text-status-delivered" : m.status === "in_progress" ? "bg-status-pending/10 text-status-pending" : "bg-muted"}`}>
                      {m.status?.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`maint-edit-${m.id}`}><Pencil size={12} /></Button>
                      {canDelete && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`maint-del-${m.id}`}><Trash2 size={12} /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Delete this record?</AlertDialogTitle><AlertDialogDescription>Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del.mutate(m.id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {(!data?.items || data.items.length === 0) && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  <Wrench size={32} className="mx-auto mb-2 opacity-40" />
                  No service records. Log routine servicing, breakdowns, tyre replacements etc. to enable service-due alerts.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </>
  );
}
